import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { DashboardModuleId } from '../types';
import type { ResearchEvent } from '../types/events';
import type { ActivityEntry, OperatorProfile, ProfileViewMode, RealProfile } from '../types/profiles';
import { useRealProfiles } from '../hooks/useRealProfiles';
import {
  filterProfiles,
  filterProfilesByDate,
  eventDateRange,
  indexOperatorsByRoomId,
  matchesViewMode,
  type AgeRange,
} from '../utils/profileFilter';

export interface ProfileCounts {
  total: number;
  managed: number;
  professor: number;
  paiMae: number;
}

interface DashboardContextValue {
  event: ResearchEvent;
  activeModule: DashboardModuleId;
  setActiveModule: (id: DashboardModuleId) => void;
  realProfiles: RealProfile[];
  operators: OperatorProfile[];
  activity: ActivityEntry[];
  profilesLoading: boolean;
  profileViewMode: ProfileViewMode;
  setProfileViewMode: (mode: ProfileViewMode) => void;
  individualProfileId: string | undefined;
  setIndividualProfileId: (id: string | undefined) => void;
  ageRange: AgeRange;
  setAgeRange: (range: AgeRange) => void;
  /** "masculino" | "feminino" | undefined (sem filtro). */
  sexFilter: string | undefined;
  setSexFilter: (sex: string | undefined) => void;
  filteredProfiles: RealProfile[];
  /** Todos os perfis (qualquer `kind`/papel) dentro do período do evento — pros
   *  totais gerais do card de topo (participantes/registros), não pros módulos de
   *  análise detalhada (esses usam `filteredProfiles`, que respeita a visão
   *  aluno/professor/pai-mãe escolhida no filtro). */
  eventProfiles: RealProfile[];
  /** Quantidade de participantes por papel, dentro dos mesmos filtros de
   *  data/idade/gênero de `filteredProfiles` — mas SEM a restrição de papel
   *  (mesma base de "todos"), pra mostrar a quebra aluno/professor/pai-mãe
   *  de uma vez, independente de qual "Visão" está selecionada no filtro. */
  profileCounts: ProfileCounts;
}

const DashboardContext = createContext<DashboardContextValue | null>(null);

interface DashboardProviderProps {
  event: ResearchEvent;
  children: ReactNode;
}

export function DashboardProvider({ event, children }: DashboardProviderProps) {
  const [activeModule, setActiveModule] = useState<DashboardModuleId>('general');
  const { profiles: realProfiles, operators, activity, loading: profilesLoading } = useRealProfiles();
  const [profileViewMode, setProfileViewMode] = useState<ProfileViewMode>('todos');
  const [individualProfileId, setIndividualProfileId] = useState<string | undefined>(undefined);
  const [ageRange, setAgeRange] = useState<AgeRange>({});
  const [sexFilter, setSexFilter] = useState<string | undefined>(undefined);

  const dateRange = useMemo(
    () => eventDateRange(Boolean(event.noPeriod), event.startDate, event.endDate),
    [event.noPeriod, event.startDate, event.endDate]
  );

  const filteredProfiles = useMemo(
    () =>
      filterProfiles(realProfiles, profileViewMode, {
        individualId: individualProfileId,
        dateRange,
        ageRange,
        sex: sexFilter,
        operators,
      }),
    [realProfiles, profileViewMode, individualProfileId, dateRange, ageRange, sexFilter, operators]
  );

  const eventProfiles = useMemo(
    () => filterProfilesByDate(realProfiles, dateRange),
    [realProfiles, dateRange]
  );

  const profileCounts = useMemo<ProfileCounts>(() => {
    const base = filterProfiles(realProfiles, 'todos', { dateRange, ageRange, sex: sexFilter, operators });
    const operatorsByRoomId = indexOperatorsByRoomId(operators);
    let managed = 0;
    let professor = 0;
    let paiMae = 0;
    for (const p of base) {
      if (matchesViewMode(p, 'managed', operatorsByRoomId)) managed++;
      else if (matchesViewMode(p, 'professor', operatorsByRoomId)) professor++;
      else if (matchesViewMode(p, 'pai_mae', operatorsByRoomId)) paiMae++;
    }
    return { total: base.length, managed, professor, paiMae };
  }, [realProfiles, operators, dateRange, ageRange, sexFilter]);

  return (
    <DashboardContext.Provider
      value={{
        event,
        activeModule,
        setActiveModule,
        realProfiles,
        operators,
        activity,
        profilesLoading,
        profileViewMode,
        setProfileViewMode,
        individualProfileId,
        setIndividualProfileId,
        ageRange,
        setAgeRange,
        sexFilter,
        setSexFilter,
        filteredProfiles,
        eventProfiles,
        profileCounts,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const ctx = useContext(DashboardContext);
  if (!ctx) {
    throw new Error('useDashboard deve ser usado dentro de DashboardProvider');
  }
  return ctx;
}
